import { useId, useState } from "react";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { MetadataSelect } from "@/components/itsm-config/metadata-select";
import { useDraft } from "@/components/itsm-config/draft-context";
import { useFreshserviceGroups } from "@/hooks/use-itsm-metadata";

interface Props {
  tenantId: string;
  credentialId: string;
  groupIds: number[] | null | undefined;
}

/** Shares the page draft so group changes use the same Save and Discard. */
export function FreshserviceGroupFilter({
  tenantId,
  credentialId,
  groupIds,
}: Props) {
  const { update } = useDraft();
  const groups = useFreshserviceGroups(tenantId, credentialId);
  const [error, setError] = useState<string | null>(null);
  const titleId = useId();
  const noteId = useId();
  const pickerId = useId();
  const restricted = groupIds != null;
  const selected = groupIds ?? [];
  const options = groups.data?.options ?? [];

  const addGroup = (value: string) => {
    const id = Number(value);
    if (!/^[1-9]\d*$/.test(value) || !Number.isSafeInteger(id)) {
      setError("Enter a positive numeric group ID.");
      return;
    }
    setError(null);
    update({ inbound_group_ids: [...new Set([...selected, id])] });
  };

  return (
    <div className="space-y-3 rounded-md border px-4 py-3">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <p id={titleId} className="text-sm font-medium">
            Limit imports to agent groups
          </p>
          <p id={noteId} className="text-muted-foreground text-sm">
            {restricted
              ? "Only tickets and changes assigned to selected groups are " +
                "imported, within the watched workspaces. Unassigned " +
                "records are excluded."
              : "Import from all agent groups within the watched workspaces."}
          </p>
        </div>
        <Switch
          checked={restricted}
          aria-labelledby={titleId}
          aria-describedby={noteId}
          onCheckedChange={(enabled) => {
            setError(null);
            update({ inbound_group_ids: enabled ? [] : null });
          }}
        />
      </div>
      {restricted && (
        <>
          <label htmlFor={pickerId} className="text-sm font-medium">
            Agent groups
          </label>
          <MetadataSelect
            id={pickerId}
            aria-label="Add an agent group"
            options={options.filter((o) => !selected.includes(Number(o.value)))}
            value=""
            onChange={addGroup}
            isLoading={groups.isLoading}
            placeholder="Select a group or enter its ID..."
            aria-invalid={Boolean(error)}
          />
          {groups.isError && (
            <p className="text-muted-foreground text-sm">
              Groups could not be loaded. You can enter a numeric group ID.
            </p>
          )}
          {groups.data?.from_ticket_form && (
            <p className="text-muted-foreground text-sm">
              This Freshservice API key cannot list agent groups, so these names
              come from the ticket form and may be incomplete. Give the key
              group access for the full list, or enter a numeric group ID.
            </p>
          )}
          {error && (
            <p role="alert" className="text-destructive text-sm">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            {selected.map((id) => {
              const label =
                options.find((o) => o.value === String(id))?.label ??
                `Group ${id}`;
              return (
                <Button
                  key={id}
                  variant="secondary"
                  size="sm"
                  aria-label={`Remove ${label}`}
                  onClick={() => {
                    update({
                      inbound_group_ids: selected.filter((v) => v !== id),
                    });
                  }}
                >
                  {label}
                  <span className="text-muted-foreground">{id}</span>
                  <X className="h-3.5 w-3.5" />
                </Button>
              );
            })}
          </div>
          {selected.length === 0 && (
            <p role="status" className="text-sm text-amber-600">
              No groups selected. Saving this selection stops importing tickets
              and changes. Select a group or turn off the group limit.
            </p>
          )}
        </>
      )}
    </div>
  );
}
